import React, { useState, useEffect, useRef } from 'react';

const AddFeeTypeModal = ({ isOpen, onClose, onSave, existingFeeTypes = [] }) => {
  const [feeTypes, setFeeTypes] = useState([]);
  const modalRef = useRef(null);

  useEffect(() => {
    // IMPORTANT:
    // Only initialize when the modal opens.
    // Parent often passes a freshly-created array each render which would otherwise
    // reset local state and make "remove row" appear broken.
    if (!isOpen) return;

    if (existingFeeTypes.length > 0) {
      setFeeTypes(existingFeeTypes);
      return;
    }

    setFeeTypes([
      { id: 1, name: 'Fee per study' },
      { id: 2, name: 'Fee per each use' },
      { id: 3, name: 'Fee per subject' }
    ]);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleAddFeeType = () => {
    const newId = Math.max(...feeTypes.map(f => f.id), 0) + 1;
    setFeeTypes([...feeTypes, { id: newId, name: '' }]);
  };

  const handleRemoveFeeType = (id) => {
    setFeeTypes(feeTypes.filter(f => f.id !== id));
  };

  const handleChangeFeeType = (id, value) => {
    setFeeTypes(feeTypes.map(f => f.id === id ? { ...f, name: value } : f));
  };

  const handleSave = () => {
    const validFeeTypes = feeTypes.filter(f => f.name.trim() !== '');
    onSave(validFeeTypes);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div ref={modalRef} className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
        {/* Modal Content */}
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-semibold text-gray-900">Add Fee Type</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Fee Types List */}
          <div className="space-y-3 mb-6">
            {feeTypes.map((feeType) => (
              <div key={feeType.id} className="flex items-center gap-3">
                {/* Drag Handle */}
                <button className="text-gray-400 hover:text-gray-600 cursor-move">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="7" cy="5" r="1.5" fill="currentColor"/>
                    <circle cx="13" cy="5" r="1.5" fill="currentColor"/>
                    <circle cx="7" cy="10" r="1.5" fill="currentColor"/>
                    <circle cx="13" cy="10" r="1.5" fill="currentColor"/>
                    <circle cx="7" cy="15" r="1.5" fill="currentColor"/>
                    <circle cx="13" cy="15" r="1.5" fill="currentColor"/>
                  </svg>
                </button>

                {/* Input Field */}
                <input
                  type="text"
                  value={feeType.name}
                  onChange={(e) => handleChangeFeeType(feeType.id, e.target.value)}
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
                  placeholder="Enter fee type name"
                />

                {/* Delete Button */}
                <button
                  onClick={() => handleRemoveFeeType(feeType.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2.5 5H4.16667H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.8333 4.99999V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83332C5.39129 18.3333 4.96737 18.1577 4.65481 17.8452C4.34225 17.5326 4.16666 17.1087 4.16666 16.6667V4.99999M6.66666 4.99999V3.33332C6.66666 2.89129 6.84225 2.46737 7.15481 2.15481C7.46737 1.84225 7.89129 1.66666 8.33332 1.66666H11.6667C12.1087 1.66666 12.5326 1.84225 12.8452 2.15481C13.1577 2.46737 13.3333 2.89129 13.3333 3.33332V4.99999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* Add Fee Type Button */}
          <button
            onClick={handleAddFeeType}
            className="flex items-center gap-2 text-emerald-400 text-sm font-medium hover:text-emerald-500 transition-colors mb-8"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6.66667V13.3333M6.66667 10H13.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Add Fee Type
          </button>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 text-base font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-emerald-400 text-white text-base font-medium rounded-xl hover:bg-emerald-500 transition-colors shadow-sm"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFeeTypeModal;

